// userSession.js — Logged-in user guard for write operations.
//
// A booking / order / payment row must never be written with userid 0 (or a
// missing id). If the session user can't be read at submit time, hard-refresh
// ONCE to re-hydrate sessionStorage; if it's still missing after that refresh,
// send the operator back to the login page instead of looping forever.

import { toast } from 'react-toastify';

const RELOAD_FLAG = 'userReloadAttempted';

// Reads the logged-in user id fresh from sessionStorage (not a stale
// module-level snapshot). Returns a positive integer, or 0 when invalid.
export function getLoggedInUserId() {
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const id = parseInt(user?.id, 10);
    return Number.isFinite(id) && id > 0 ? id : 0;
  } catch {
    return 0;
  }
}

// Returns a valid (> 0) user id, or null after triggering recovery.
// Callers MUST abort their action when this returns null.
export function requireUserId() {
  const id = getLoggedInUserId();
  if (id > 0) {
    sessionStorage.removeItem(RELOAD_FLAG);
    return id;
  }
  if (!sessionStorage.getItem(RELOAD_FLAG)) {
    // First failure: user details likely not hydrated yet — hard refresh once.
    sessionStorage.setItem(RELOAD_FLAG, 'true');
    window.location.reload();
    return null;
  }
  // Still no valid user after a refresh — don't loop; force re-login.
  sessionStorage.removeItem(RELOAD_FLAG);
  toast.error('Your session could not be loaded. Please log in again.');
  window.location.href = '/login';
  return null;
}
